import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { ReactComponent as GogoLogoIcon } from '../../assets/GogoLogoIcon.svg';
import { ReactComponent as LogoutIcon } from '../../assets/LogoutIcon.svg';
import { theme } from '../../theme/theme';
import { useGetCloudSystemSummariesQuery, uncommissionedSummariesSlice } from "../../features/dashboard/apiDashboard";
import { useDispatch, useSelector } from "react-redux";
import SoftwareManagementService from '../../features/software/apiSoftwareManagement';
import { Menu, MenuItem } from "@mui/material"
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import {
  AppBar,
  Badge,
  Box,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';

export const Pages = {
  FBO : {
    key: 'fbo',
    name:'FBO',
    title: 'Summaries - FBO',
    path: '/summaries/fbo'
  },
  CloudPort: {
    key: 'cloudport',
    name: 'Cloudports',
    title: 'Summaries - Cloudports',
    path: '/summaries/cloudport'
  },
  Uncommissioned: {
    key:'uncommissioned',
    name: 'Uncommissioned',
    title: 'Summaries - Uncommissioned',
    path: '/summaries/uncommissioned'
  },
  Software: {
    key:'software',
    name: 'Software',
    title: 'Software',
    path: '/software'
  }
};

const ResponsiveAppBar = () => {

  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [permissions,setPermissions] = React.useState({isSet:false,isAdmin:false});
  const dispatch = useDispatch();

  let curPage = Pages.FBO;

  if (window.location.pathname.includes(Pages.CloudPort.key)) {
    curPage = Pages.CloudPort
  } else if (window.location.pathname.includes(Pages.Uncommissioned.key)) {
    curPage = Pages.Uncommissioned;
  } else if (window.location.pathname.includes(Pages.Software.key)) {
    curPage = Pages.Software;
  }

  document.title = curPage.title;

  const [currentPage, setCurrentPage] = React.useState(curPage);  

  const navigateWithTitle = (route, title) => {
    document.title = title;
    navigate(route);
  };

  const handleSignOutClick = () => {
    instance.logoutRedirect().catch(e => {
      console.error(e);
    });
  };

  // Set up the poll so that the count updates when we get new data
  useGetCloudSystemSummariesQuery({type: "uncommissioned"}, { refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true, pollingInterval: 10000  });

  React.useEffect(() => {
    let newPermissions = {
      isSet: false,
      isAdmin: false,
    }

    const fetchIsAdmin = async () => {
      const result = await SoftwareManagementService.canAccessSoftwareBackend();      
      newPermissions.isAdmin = result;
      setPermissions(newPermissions)
    };
    if(!permissions.isSet)
    {
      newPermissions.isSet = true;
      fetchIsAdmin();
    }    
  });

  const uncommissionedSummaryCount = useSelector(state => state.uncommissionedSummariesSlice.uncommissionedSummaryCount);

 const createListItemButton = (page) => {
    return (<ListItemButton
    onClick={() => {
      navigateWithTitle(page.path, page.title);
      setCurrentPage(page);
    }}
    sx={{ px: 5, display: 'flex' }}
  >
    <ListItemText
      primary={page.name}
      primaryTypographyProps={{
        sx: {
          fontSize: '1rem',
          fontWeight: 'bold',
          borderBottom: currentPage.key === page.key ? '2px solid white' : 'none',
        },
      }}
    />
  </ListItemButton>);
  };

  const createListItemButtonWithBadge = (page,badgeContent) => {
    return (<ListItemButton
              onClick={() => {
                navigateWithTitle(page.path, page.title);
                setCurrentPage(page);
              }}
              sx={{ px: 5, display: 'flex' }}
            >
            <Badge badgeContent={badgeContent ?? "0"} 
              sx={{ 
                "& .MuiBadge-badge": {
                  color: theme.palette.tertiary.contrastText, 
                  backgroundColor: theme.palette.tertiary.main,
                  height: "15px",
                  width: "15px",
                  minWidth: "15px!important"
                }
              }}>
                  <ListItemText
                    primary={page.name}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        borderBottom: currentPage.key === page.key ? '2px solid white' : 'none',
                      },
                    }}
                  />
            </Badge>
            </ListItemButton>);
  };

  const adminPages = [createListItemButton(Pages.FBO), createListItemButton(Pages.CloudPort), createListItemButtonWithBadge(Pages.Uncommissioned,uncommissionedSummaryCount),createListItemButton(Pages.Software)];
  const pages = [createListItemButton(Pages.FBO), createListItemButton(Pages.CloudPort), createListItemButtonWithBadge(Pages.Uncommissioned,uncommissionedSummaryCount)];

  // Dropdown menu handling
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <AppBar position="fixed" sx={{top: 0, height: 80, p: 0}}>
      <Container maxWidth="false" sx={{height: 80}}>
        <Toolbar disableGutters sx={{height: 80}}>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: 'flex',
              color: 'inherit',
              textDecoration: 'none',
              flexShrink: 0,
            }}
          >
            <GogoLogoIcon width={101.4585} height={47.733} />
          </Typography>

          <List sx={{display: 'flex', flexGrow: 0, alignItems: 'center',
            // hover states
            '& .MuiListItemButton-root:hover': {
              '&, & .MuiListItemText-root': {
                color: theme.palette.tertiary.main,
              },
            },
          }}>
            {permissions.isAdmin ? adminPages.map((page, index) => (
              <ListItem 
                disablePadding
                key={index}>
                {page}
              </ListItem>
            )) : pages.map((page, index) => (
              <ListItem 
                disablePadding
                key={index}>
                {page}
              </ListItem>
            ))}
          </List>

          {/* Add a horizontal spacer to push the rest of the components to the sides */}
          <Container sx={{ flexGrow: 1 }}/>
          
          <Box sx={{ flexGrow: 0, marginLeft: "auto" }}>
            <Grid container direction='row' alignItems={'center'} wrap='nowrap' onClick={handleMenuClick} sx={{ cursor: 'pointer' }}>
              <Grid item sx={{ml: 1}}>
                <Typography fontSize='1rem' fontWeight={700} sx={{whiteSpace: 'nowrap'}}>
                  {accounts?.at(0)?.name}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton sx={{ p: 0 }}>
                  <ArrowDropDownIcon sx={{ color: 'white' }} />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
          <Menu
            id="basic-menu"
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            PaperProps={{
              sx: {
                color: theme.palette.tertiary.contrastText, 
                backgroundColor: '#0B1826',
                border: '1px solid #505050'
              }
            }}
          >
            <MenuItem onClick={handleSignOutClick}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
            <MenuItem sx={{ borderTop: '1px solid #505050' }}>App Version {process.env.REACT_APP_VERSION}</MenuItem>
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
export default ResponsiveAppBar;
